// apps/portal/src/components/Schedule/scheduleModel.ts
import dayjs from 'dayjs';
// Importing this also extends dayjs with utc + timezone (tests never load main.tsx).
import { toIsoFromLocal } from 'src/components/Timesheet/timesheetFormat';
import { readErrorCode } from 'src/services/apis/errors';
import { MyScheduleDto, ScheduleShiftDto } from 'src/services/apis/types';

/**
 * Client shapes for shift scheduling (server: apps/server/src/schedules/dto/schedule.dto.ts)
 * and the pure helpers the calendar and dialogs share. Instants are ISO strings; every
 * wall-clock answer names the restaurant's zone.
 */

export type PlannedAssignee = {
  assignmentId: string;
  employeeId: string;
  employeeName: string;
  source: 'ASSIGNED' | 'REGISTERED';
  timeEntryId: string | null;
  timesheetId: string | null;
  late: boolean;
  noShow: boolean;
};

export type PlannedShift = {
  id: string;
  branchId: string;
  startAt: string;
  endAt: string;
  shiftName: string | null;
  note: string | null;
  headcount: number;
  seatsLeft: number;
  published: boolean;
  assignees: PlannedAssignee[];
};

export type StaffMember = { employeeId: string; employeeName: string; branchId: string };

export type MyShift = {
  shiftId: string;
  branchId: string;
  startAt: string;
  endAt: string;
  shiftName: string | null;
  note: string | null;
  headcount: number;
  seatsLeft: number;
  /** true = the caller holds a seat; false = an open shift they may take. */
  mine: boolean;
  source: 'ASSIGNED' | 'REGISTERED' | null;
  canWithdraw: boolean;
  withdrawBy: string;
};

/** What a clock-in response carries when it matched a planned shift (server scheduleMatching.ts). */
export type MatchedShift = {
  assignmentId: string;
  shiftId: string;
  shiftName: string | null;
  startAt: string;
  endAt: string;
};

/** An entry's label on the timesheet detail (server EntryScheduleDto). */
export type EntrySchedule = {
  link: {
    assignmentId: string;
    shiftId: string;
    shiftName: string | null;
    startAt: string;
    endAt: string;
    late: boolean;
  } | null;
  unscheduled: boolean;
};

export type PlannedAttention =
  | { kind: 'noShow'; shift: PlannedShift; assignee: PlannedAssignee }
  | { kind: 'unfilled'; shift: PlannedShift };

const DATE = 'YYYY-MM-DD';
const HOUR = 3_600_000;

export function plannedFromApi(dto: ScheduleShiftDto): PlannedShift {
  return {
    id: dto.id,
    branchId: dto.branchId,
    startAt: dto.startAt,
    endAt: dto.endAt,
    shiftName: dto.shiftName ?? null,
    note: dto.note ?? null,
    headcount: dto.headcount,
    seatsLeft: dto.seatsLeft,
    published: dto.published,
    assignees: (dto.assignees ?? []).map((a) => ({
      assignmentId: a.assignmentId,
      employeeId: a.employeeId,
      employeeName: a.employeeName,
      source: a.source as PlannedAssignee['source'],
      timeEntryId: a.timeEntryId ?? null,
      timesheetId: a.timesheetId ?? null,
      late: a.late,
      noShow: a.noShow,
    })),
  };
}

export function myShiftsFromApi(dto: MyScheduleDto | undefined): MyShift[] {
  return (dto?.shifts ?? []).map((s) => ({
    shiftId: s.shiftId,
    branchId: s.branchId,
    startAt: s.startAt,
    endAt: s.endAt,
    shiftName: s.shiftName ?? null,
    note: s.note ?? null,
    headcount: s.headcount,
    seatsLeft: s.seatsLeft,
    mine: s.mine,
    source: (s.source ?? null) as MyShift['source'],
    canWithdraw: s.canWithdraw,
    withdrawBy: s.withdrawBy,
  }));
}

const KNOWN = new Set([
  'SHIFT_FULL',
  'SHIFT_CONFLICT',
  'SHIFT_STARTED',
  'CANCEL_TOO_LATE',
  'SHIFT_NOT_PUBLISHED',
  'SHIFT_INVALID',
  'SCHEDULE_NEEDS_BRANCH',
  'WEEK_INVALID',
  'NOT_HOURLY_EMPLOYEE',
  'CALENDAR_RANGE_INVALID',
]);

export function scheduleErrorKey(error: unknown): string {
  const code = readErrorCode(error);
  return code && KNOWN.has(code) ? `schedule.errors.${code}` : 'schedule.errors.generic';
}

/** FullCalendar runs in UTC mode on restaurant-local floating strings; this is the instant behind one. */
export function fromFloating(floating: string, tz: string): string {
  return toIsoFromLocal(floating.slice(0, 10), floating.slice(11, 16), tz);
}

/** A wall-clock date and two HH:mm → an ISO span; an end at or before the start ends the next day. */
export function spanFromLocal(
  date: string,
  start: string,
  end: string,
  tz: string
): { startAt: string; endAt: string } {
  const startAt = toIsoFromLocal(date, start, tz);
  let endAt = toIsoFromLocal(date, end, tz);
  if (endAt <= startAt) endAt = toIsoFromLocal(dayjs.utc(date).add(1, 'day').format(DATE), end, tz);
  return { startAt, endAt };
}

type Span = { startAt: string; endAt: string };
const overlap = (a: Span, b: Span) => a.startAt < b.endAt && b.startAt < a.endAt;

/** Mirrors the server's SHIFT_CONFLICT so a dialog can disable a person before asking. */
export function conflictFor(
  employeeId: string,
  span: Span,
  shifts: PlannedShift[],
  exceptShiftId?: string
): 'SHIFT_CONFLICT' | null {
  const busy = shifts.some(
    (s) => s.id !== exceptShiftId && s.assignees.some((a) => a.employeeId === employeeId) && overlap(s, span)
  );
  return busy ? 'SHIFT_CONFLICT' : null;
}

/**
 * Monday..Sunday around a `YYYY-MM-DD` — date-only arithmetic must run in UTC, never host-local
 * (`dayjs(date).day()` reads the browser's zone, which can shift which calendar day `date` falls
 * on and thus which Monday it belongs to).
 */
export function weekOf(date: string): { from: string; to: string } {
  const day = dayjs.utc(date);
  const monday = day.subtract((day.day() + 6) % 7, 'day');
  return { from: monday.format(DATE), to: monday.add(6, 'day').format(DATE) };
}

/** The day before to the day after — what a dialog loads to see who is busy (shifts cross midnight). */
export function aroundDay(date: string): { from: string; to: string } {
  return { from: dayjs.utc(date).subtract(1, 'day').format(DATE), to: dayjs.utc(date).add(1, 'day').format(DATE) };
}

export function localDate(iso: string, tz: string): string {
  return dayjs(iso).tz(tz).format(DATE);
}

/**
 * Drafts whose restaurant-local start date lies in [from, to] AND have not started yet: the
 * server's publish skips drafts that already started (Ruling 1), so counting them here would
 * leave "Publish week (n drafts)" stuck above the number publish will actually act on.
 */
export function draftsIn(
  shifts: PlannedShift[],
  from: string,
  to: string,
  tz: string,
  now: Date = new Date()
): PlannedShift[] {
  return shifts.filter((s) => {
    const day = localDate(s.startAt, tz);
    return !s.published && day >= from && day <= to && new Date(s.startAt).getTime() > now.getTime();
  });
}

export function peopleIn(shifts: PlannedShift[]): number {
  return new Set(shifts.flatMap((s) => s.assignees.map((a) => a.employeeId))).size;
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return `${words[0][0]}${last}`.toUpperCase();
}

/** Spec §7.1 sidebar: every no-show, and published shifts with empty seats that start within 48 h. */
export function plannedAttention(shifts: PlannedShift[], now: Date = new Date()): PlannedAttention[] {
  const items: PlannedAttention[] = [];
  for (const shift of shifts) {
    for (const assignee of shift.assignees) if (assignee.noShow) items.push({ kind: 'noShow', shift, assignee });
    const untilStart = new Date(shift.startAt).getTime() - now.getTime();
    if (shift.published && shift.seatsLeft > 0 && untilStart > 0 && untilStart <= 48 * HOUR) {
      items.push({ kind: 'unfilled', shift });
    }
  }
  return items.sort((a, b) => a.shift.startAt.localeCompare(b.shift.startAt));
}

/** The caller's next own shift that has not ended yet. */
export function nextShift(shifts: MyShift[], now: Date = new Date()): MyShift | null {
  return (
    shifts
      .filter((s) => s.mine && new Date(s.endAt).getTime() > now.getTime())
      .sort((a, b) => a.startAt.localeCompare(b.startAt))[0] ?? null
  );
}

/**
 * Spec §2 manager sidebar: planned person-minutes (shift duration × headcount) and filled
 * person-minutes (duration × actual assignees) over the given shifts — the caller passes only
 * the shifts already scoped to the visible range. Minutes, not hours, so callers can reuse
 * `formatMinutes` for display. Duration is an instant diff, so an overnight shift (e.g. 22:00 →
 * 06:00 the next day) is unaffected by date-only arithmetic.
 */
export function plannedFilledMinutes(shifts: PlannedShift[]): { planned: number; filled: number } {
  let planned = 0;
  let filled = 0;
  for (const s of shifts) {
    const min = (new Date(s.endAt).getTime() - new Date(s.startAt).getTime()) / 60_000;
    planned += min * s.headcount;
    filled += min * s.assignees.length;
  }
  return { planned, filled };
}

/** Open shifts (not mine, seats left, not started) whose local start date lies in [from, to]. */
export function openIn(shifts: MyShift[], from: string, to: string, tz: string, now: Date = new Date()): MyShift[] {
  return shifts.filter((s) => {
    const day = localDate(s.startAt, tz);
    return !s.mine && s.seatsLeft > 0 && new Date(s.startAt).getTime() > now.getTime() && day >= from && day <= to;
  });
}
